import type { Ref } from 'vue'

import type { VxeGridProps } from '@vben/plugins/vxe-table'

import { get, isNil } from 'lodash-es'

// radio selection 是 adapter 的 opt-in 能力，用外部 model 保存当前选中行。
export interface RadioSelectionOptions<T> {
  type: 'radio'
  model: Ref<null | T | undefined>
  keyField?: string
  onChange?: (row: T) => void
  radioConfig?: Record<string, any>
  column?: Record<string, any>
}

// 只识别显式配置的 radio selection，普通表格不会进入这段逻辑。
export function resolveRadioSelection<T>(selection: unknown) {
  return typeof selection === 'object' &&
    selection !== null &&
    (selection as RadioSelectionOptions<T>).type === 'radio'
    ? (selection as RadioSelectionOptions<T>)
    : undefined
}

export function createRadioSelection<T>({
  getDefaultKeyField,
  getGridApi,
  selection,
}: {
  getDefaultKeyField: () => string
  getGridApi: () => any
  selection: RadioSelectionOptions<T>
}) {
  const getKeyField = () =>
    selection.keyField ??
    getGridApi()?.grid?.rowConfig?.keyField ??
    getDefaultKeyField()

  // 注入 radio 列，并保留调用方已有的 radioConfig。
  const applyGridOptions = (
    gridOptions: VxeGridProps<T>['gridOptions'] = {},
  ) => ({
    ...gridOptions,
    columns: [
      {
        field: '__radio__',
        width: 56,
        ...selection.column,
        type: 'radio' as const,
      },
      ...(gridOptions.columns ?? []).filter(
        (column: any) => column?.type !== 'radio',
      ),
    ],
    radioConfig: {
      highlight: true,
      reserve: true,
      ...gridOptions.radioConfig,
      ...selection.radioConfig,
    },
    rowConfig: {
      ...gridOptions.rowConfig,
      keyField: getKeyField(),
    },
  })

  // 包装 radioChange，让外部 model 和原有事件处理同时生效。
  const applyGridEvents = (gridEvents: VxeGridProps<T>['gridEvents'] = {}) => {
    const oldRadioChange = gridEvents.radioChange as
      | ((params: any) => void)
      | undefined

    return {
      ...gridEvents,
      radioChange: (params: any) => {
        selection.model.value = params.row
        selection.onChange?.(params.row)
        oldRadioChange?.(params)
      },
    }
  }

  // 数据重新加载后，根据 keyField 找回当前页中的已选行。
  const restore = (grid: any) => {
    if (!selection.model.value) {
      return
    }

    const selectedKey = get(selection.model.value, getKeyField())
    if (isNil(selectedKey)) {
      return
    }

    const records = grid?.getTableData?.().tableData ?? []
    const currentRow = records.find(
      (item: any) => get(item, getKeyField()) === selectedKey,
    )
    if (currentRow) {
      grid?.setRadioRow?.(currentRow)
    }
  }

  return {
    applyGridEvents,
    applyGridOptions,
    restore,
  }
}
